import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { authApi, type OAuthProvider } from "@/lib/api/auth";
import { createPkcePair, savePendingSignIn } from "@/lib/pkce";

/** Google "G" mark (official four-colour logo). */
function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** Microsoft four-square mark. */
function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

const PROVIDER_UI: Record<OAuthProvider, { label: string; Logo: () => React.JSX.Element }> = {
  google: { label: "Continue with Google", Logo: GoogleLogo },
  microsoft: { label: "Continue with Microsoft", Logo: MicrosoftLogo },
};

/** Which external providers the server has configured. */
export function useSignInProviders() {
  return useQuery({
    queryKey: ["auth", "providers"],
    queryFn: () => authApi.providers(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** One "Continue with <provider>" button — starts the PKCE authorization-code flow. */
function ProviderButton({
  provider,
  onError,
}: {
  provider: OAuthProvider;
  onError?: ((message: string) => void) | undefined;
}) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { label, Logo } = PROVIDER_UI[provider];

  const start = async () => {
    setIsRedirecting(true);
    try {
      const { verifier, challenge } = await createPkcePair();
      const { url, state } = await authApi.oauthUrl(provider, challenge);
      savePendingSignIn(provider, { state, verifier });
      window.location.assign(url);
    } catch (error) {
      setIsRedirecting(false);
      onError?.(error instanceof Error ? error.message : "Could not start sign-in.");
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full gap-2"
      disabled={isRedirecting}
      onClick={() => {
        void start();
      }}
    >
      <Logo />
      {isRedirecting ? "Redirecting..." : label}
    </Button>
  );
}

/**
 * "Continue with Google" / "Continue with Microsoft" (work accounts). Each
 * button renders only when the server has that provider configured; nothing
 * renders (not even the divider) when none are.
 */
export function ExternalSignInButtons({
  onError,
  withDivider = true,
}: {
  onError?: (message: string) => void;
  withDivider?: boolean;
}) {
  const { data: providers } = useSignInProviders();
  const enabled = (["google", "microsoft"] as const).filter((p) => providers?.[p]);
  if (enabled.length === 0) return null;
  return (
    <div className="space-y-3">
      {withDivider && <OrDivider />}
      {enabled.map((provider) => (
        <ProviderButton key={provider} provider={provider} onError={onError} />
      ))}
    </div>
  );
}

/** "OR" divider between the email form and the provider buttons. */
export function OrDivider() {
  return (
    <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      or
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
